import { useEffect, useState } from 'react'
import { Box, Card, Text, Group, Stack } from '@mantine/core'
import { useI18n } from '../_i18n'

// App semantic colors (styleguide §3): tile/background blue encodes TEMPORAL
// POSITION (last/next/following), never polarity. Same blues in light + dark.
export const TILE = {
  bright: '#157fb4', // last / current
  mid: '#105f87', // next
  dark: '#0a405a', // following / sun
  moon: '#05202d', // moon
  weather: '#2f6b7e', // weather band — distinct from the temporal blues + moon
}

const GOLD = '#FFD966'
const SUB = { color: 'rgba(255,255,255,0.7)' }

export function Tile({ bg, children }) {
  return (
    <Card padding="lg" radius="md" style={{ background: bg, color: '#fff', height: '100%' }}>
      {children}
    </Card>
  )
}

// One arrow, rotated (styleguide §6.3): tide high = up (0°), low = down (180°);
// currents rotate to the flow compass bearing. Gold, per the app.
export function DirectionArrow({ deg = 0, size = 18 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      style={{ transform: `rotate(${deg}deg)`, flexShrink: 0 }}
      aria-hidden="true"
    >
      <path
        d="M12 4 V20 M12 4 L6.5 9.5 M12 4 L17.5 9.5"
        stroke={GOLD}
        strokeWidth={2.6}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

// 16-point compass from a bearing (config DirectionArrow.degreesToCompass).
const COMPASS = [
  'N',
  'NNE',
  'NE',
  'ENE',
  'E',
  'ESE',
  'SE',
  'SSE',
  'S',
  'SSW',
  'SW',
  'WSW',
  'W',
  'WNW',
  'NW',
  'NNW',
]
export function compass16(deg) {
  if (deg == null) return null
  return COMPASS[Math.round((((deg % 360) + 360) % 360) / 22.5) % 16]
}

// The app's current direction + speed cluster: "WNW ↗ 3.4 kt" — compass +
// gold arrow rotated to the bearing + speed. Slack / no direction → speed only.
// `color` sets the text color (white on the colored Now tiles; theme default in
// the week table). The arrow is always gold.
export function DirLine({ deg, speed, color, fz = '1.3rem', arrow = 22 }) {
  const compass = compass16(deg)
  if (!compass && !speed) return null
  const txt = { color: color || undefined }
  return (
    <Group gap={7} align="center" wrap="nowrap" justify="center">
      {compass && (
        <Text fz={fz} fw={600} style={txt}>
          {compass}
        </Text>
      )}
      {deg != null && <DirectionArrow deg={deg} size={arrow} />}
      {speed && (
        <Text fz={fz} fw={600} style={txt}>
          {speed}
        </Text>
      )}
    </Group>
  )
}

// The app's relative time (config/datetime.js relativeTimeFromNow): "Just now"
// (<60s), "{h} hour[s], {m} minute[s] {ago|from now}", collapsing to hour-only
// or minute-only, with singular/plural.
//
// Depends on "now", so the server's value and the client's differ by however long
// the page took to reach the browser — often enough to cross a minute boundary.
// It is still rendered on the server: the string is the same length either way, so
// server-rendering it costs nothing and reserving its line is what keeps the card
// from growing 37px at hydration and shoving the rest of the page down.
// Exported since PR 4 of the popup-system rollout: the map popups' "when"
// rows reuse this exact grammar (Randy: "we have a specific way of doing
// relative time that we should re-use").
export function relativeTimeFromNow(t, iso) {
  const to = new Date(iso).getTime()
  if (isNaN(to)) return ''
  const secondsDiff = (to - Date.now()) / 1000
  if (Math.abs(secondsDiff) > 86400) return t('somethings_wrong')
  if (Math.abs(secondsDiff) < 60) return t('just_now')
  const h = Math.floor(Math.abs(secondsDiff / 3600))
  const min = Math.floor(Math.abs(secondsDiff / 60)) - h * 60
  const dir = secondsDiff < 0 ? 'ago' : 'from_now'
  const hWord = h === 1 ? 'hour' : 'hours'
  const mWord = min === 1 ? 'minute' : 'minutes'
  let key
  if (h && min) key = `${hWord}_${mWord}_${dir}`
  else if (h) key = `${hWord}_${dir}`
  else if (min) key = `${mWord}_${dir}`
  else return t('just_now')
  return t(key, { hour: h, minute: min })
}

// French tidal coefficient chip ("Coef 95"). SHOM stations only, high tides
// only — the payload carries `coefficient` on high-tide events for French
// stations and null everywhere else, so callers guard on non-null. A small
// rounded chip (styleguide §5), tinted to sit on the colored tide tiles/bands;
// value(s) joined with " / " for a whole day in the week view.
export function CoefPill({ children }) {
  return (
    <Box
      style={{
        display: 'inline-block',
        background: 'rgba(255,255,255,0.18)',
        color: '#fff',
        borderRadius: 8,
        padding: '1px 8px',
        fontWeight: 600,
        fontSize: '0.95rem',
        lineHeight: 1.5,
      }}
    >
      {children}
    </Box>
  )
}

// A web-only stat line under the time (tide height / current speed).
export function TileStat({ children }) {
  return children ? (
    <Text fz="1.3rem" fw={600} style={{ color: 'rgba(255,255,255,0.9)' }}>
      {children}
    </Text>
  ) : null
}

// The app's Now tile: uppercased headline (with a leading glyph OR trailing
// arrow), the big time (+ the station's timezone abbreviation), then the
// relative time. `extra` is an optional web-only line (height / speed / moon
// phase).
//
// `tzAbbr` is server-supplied and always rendered; a blocking head script hides
// it for visitors whose clock matches the station's, before anything paints.
// Doing that in CSS rather than in React is the whole point — see
// layouts/_station_timezone.html.erb. The relative time is recomputed in an
// effect, but it is server-rendered first so its line already exists.
export function NowTile({
  bg,
  glyph,
  headline,
  arrowDeg,
  afterArrow,
  time,
  iso,
  tzAbbr,
  beforeTime,
  extra,
}) {
  const { t } = useI18n()
  // Computed during render, not in the effect, so the server emits it too. The
  // effect below recomputes it on the client, which corrects the minute or two
  // that elapsed in transit.
  const [rel, setRel] = useState(() => (iso ? relativeTimeFromNow(t, iso) : ''))
  useEffect(() => {
    setRel(iso ? relativeTimeFromNow(t, iso) : '')
  }, [iso])
  return (
    <Tile bg={bg}>
      {/* Centered, big time — matching the production site's Now cards. */}
      <Stack gap={4} align="center" style={{ textAlign: 'center' }}>
        <Group gap={7} justify="center" wrap="nowrap" align="center">
          {glyph && (
            <Text fz="1.4rem" style={{ lineHeight: 1 }}>
              {glyph}
            </Text>
          )}
          <Text fz="1.7rem" fw={700} style={{ color: '#fff' }}>
            {headline}
          </Text>
          {arrowDeg != null && <DirectionArrow deg={arrowDeg} size={26} />}
          {afterArrow}
        </Group>
        {beforeTime}
        {time ? (
          <>
            <Text fz="3.75rem" fw={700} lh={1.02}>
              {time}
            </Text>
            {/* Always rendered, so it is in the first painted frame rather
              than inserted into it. data-tz-suffix is the hook the head
              script's stylesheet hides it by when this visitor's clock already
              matches the station's — see layouts/_station_timezone. */}
            {tzAbbr && (
              <Text data-tz-suffix fz="1.3rem" fw={600} lh={1} mt={-2} style={{ color: '#fff' }}>
                {tzAbbr}
              </Text>
            )}
            {extra}
            {rel && (
              // The server's "now" and the client's differ, so the text can differ
              // by a minute. That is a text swap inside a line that already exists,
              // not a layout change — suppress the warning rather than defer the
              // render and reintroduce the shift.
              <Text fz="1.3rem" style={SUB} suppressHydrationWarning>
                {rel}
              </Text>
            )}
          </>
        ) : (
          <Text style={SUB}>-</Text>
        )}
      </Stack>
    </Tile>
  )
}
